// Node Modules
import {
  useEffect, useState
} from 'react';
import React from 'react';
import moment from 'moment';
import { nanoid } from 'nanoid';

// Components
import StyledCard from '@/components/global-components/styled-card/StyledCard';

// Hooks
import useDebounce from '@/scripts/hooks/useDebounce'

// Themes
import theme from '@/themes/index';

const SearchSidebar = ({
  recentPosts,
  getSearchTerm,
  showSearchBar = true,
}) => {
  // State and setters for ...
  // Search term
  const [searchTerm, setSearchTerm] = useState('');
  // Debounce search term so that it only gives us latest value ...
  // ... if searchTerm has not been updated within last 500ms.
  // The goal is to only have the API call fire when user stops typing ...
  // ... so that we aren't hitting our API rapidly.
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Effect for API call
  useEffect(
    () => {
      if (debouncedSearchTerm) {
        getSearchTerm && getSearchTerm(searchTerm);
      } else {
        getSearchTerm && getSearchTerm(null);
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  function goToPost(post) {
    window.location.href = `/post/${post.meta.id}`;
  }

  const getPostContent = post => {
    return (
      <>
        <div className='post-container' onClick={() => goToPost(post)}>
          <p>{post.title}</p>
          <span className='details'>{moment(post.date).format('D MMMM YYYY')}</span>
        </div>
        <style jsx>{`
          div.post-container {
            display: flex;
            flex-direction: column;
            cursor: pointer;
          }

          div.post-container p {
            font-family: ${theme.fontFamilies.openSansBold};
            font-size: 1.125rem;
            line-height: 1.875rem;
            margin-block-start: 0;
            margin-block-end: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 16.1875rem;
          }

          div.post-container span.details {
            padding-top: 0.5rem;
            font-size: 1.125rem;
            line-height: 1.5rem;
          }
        `}</style>
      </>
    );
  };

  return(
    <>
      <div className='main-container'>
        {showSearchBar && 
        <React.Fragment>
          <div className='top-content'>
            <h6>Search</h6>
            <input type='text' className='search' placeholder='Search' onChange={e => setSearchTerm(e.target.value)}  />
          </div>
          <hr />
        </React.Fragment>}
        
        <div className='bottom-content'>
          <h6>Recent Posts</h6>
          <div className='posts'>
            {recentPosts?.map(post => (
              <StyledCard
                key={nanoid()}
                content={{
                  image: post.node.previewImage,
                }}
                containerStyle={{
                  flexDirection: 'row-reverse', 
                }}
                infoContainerStyle={{
                  padding: '0',
                  paddingLeft: '0',
                  textAlign: 'left',
                }}
                imageStyles={{
                  width: '4rem',
                  height: '4rem',
                }}
              >
                {getPostContent(post.node)}
              </StyledCard>
            ))}
          </div>
        </div>
      </div>
      <style jsx>{`
        div.main-container {
          display: flex;
          flex-direction: column;
        }
        div.main-container h6 {
          font-family: ${theme.fontFamilies.montserratBold};
          margin-block-start: 0;
          margin-block-end: 0;
        }

        div.main-container .search {
          min-height: 3.125rem;
          font-size: 1.25rem;
          width: 100%;
          margin-top: 2rem;
          padding-left: 0.5rem;
        }

        div.main-container hr {
          margin: 4rem 0;
        }

        div.main-container div.posts {
          padding-top: 2rem;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        div.main-container .top-content, .bottom-content {
          display: flex;
          flex-direction: column;
        }

        @media only screen and (min-width: ${theme.breakpoints.tabletMin}) {
          div.main-container {
            flex-direction: row;
            gap: 3rem;
          }

          div.main-container hr {
            margin: 0;
          }

          div.main-container .top-content, .bottom-content {
            width: 40%;
            align-items: flex-start;
          }
        }

        @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
          div.main-container {
            flex-direction: column;
            gap: 0;
          }

          div.main-container hr {
            margin: 4rem 0;
          }

          div.main-container .top-content, .bottom-content {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default SearchSidebar;
